// src/pages/projects/AddProject.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const AddProject = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    client_id: "",
    start_date: "",
    end_date: "",
    status: "new",
    action: "add",
  });

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showClientModal, setShowClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientContact, setNewClientContact] = useState("");

  /* ================= LOAD CLIENTS ================= */

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/clients.php`,
        { action: "getAll" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setClients(response.data.clients || []);
    } catch (err) {
      console.error("Failed to fetch clients", err);
    }
  };

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClientChange = (e) => {
    if (e.target.value === "add_new") {
      setShowClientModal(true);
    } else {
      setFormData({ ...formData, client_id: e.target.value });
    }
  };

  const handleAddClient = async () => {
    if (!newClientName.trim() || !newClientEmail.trim()) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/clients.php`,
        {
          name: newClientName,
          email: newClientEmail,
          contact: newClientContact.trim() || "",
          action: "addNew",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const newClient = response.data.client;
      setClients((prev) => [...prev, newClient]);
      setFormData({ ...formData, client_id: newClient.id });

      setShowClientModal(false);
      setNewClientName("");
      setNewClientEmail("");
      setNewClientContact("");
    } catch (err) {
      console.error("Failed to add client", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API_BASE_URL}/projects.php`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      alert("Project added successfully!");
      navigate("/projects");
    } catch (err) {
      console.error("Failed to add project:", err);
      setError("Failed to add project. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */

  return (
    <>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Add New Project</h1>
          <p className="text-sm text-gray-500">
            Create and assign a new project
          </p>
        </div>

        <Link
          to="/projects"
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 max-w-3xl">
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Project Name */}
          <div>
            <label className="block font-medium mb-1">Project Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block font-medium mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Client */}
          <div>
            <label className="block font-medium mb-1">Client</label>
            <select
              name="client_id"
              value={formData.client_id}
              onChange={handleClientChange}
              required
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select Client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
              <option value="add_new">➕ Add New Client</option>
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-1">Start Date</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
                className="w-full border rounded px-3 py-2"
              />
            </div>

            <div>
              <label className="block font-medium mb-1">End Date</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block font-medium mb-1">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              className="w-full border rounded px-3 py-2"
            >
              <option value="new">New</option>
              <option value="ongoing">On Going</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
            </select>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
          >
            {loading ? "Saving..." : "Add Project"}
          </button>
        </form>
      </div>

      {/* ADD CLIENT MODAL */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-md w-full max-w-sm">
            <h3 className="text-xl font-bold mb-4">Add New Client</h3>

            <input
              type="text"
              value={newClientName}
              onChange={(e) => setNewClientName(e.target.value)}
              placeholder="Client name"
              className="w-full border rounded px-3 py-2 mb-3"
            />

            <input
              type="email"
              value={newClientEmail}
              onChange={(e) => setNewClientEmail(e.target.value)}
              placeholder="Client email"
              className="w-full border rounded px-3 py-2 mb-3"
            />

            <input
              type="tel"
              value={newClientContact}
              onChange={(e) => setNewClientContact(e.target.value)}
              placeholder="Client contact (optional)"
              className="w-full border rounded px-3 py-2 mb-4"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowClientModal(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddClient}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddProject;
